import { useState } from 'react';
import Head from 'next/head';
import styled from '@emotion/styled';
import CountdownTimer from './CountdownTimer';
import { __t } from './i18n';
import { IconPlay, IconInfo } from './icons';
import { mainBg } from './styles/Main';
import { Box, Button, Flex, Icon, Link, Tag, Text, VisuallyHidden } from './styles';
import { SpacingProps } from './withStyleSystem';
import { globalPadding } from './Layout/Layout';
import { WidgetItemSlider } from './apiService';
import { getNavigationUrl, getTimerDuration, inspectFlags, usePlayerService } from './utils';
import { getPlayLabel, getPlayerUrl } from './utils/usePlayerService';
import { ViewerContentProps, useRenderedUser } from './utils/useViewerAccess';
import { Transition } from 'react-transition-group';
import { useRouter } from 'next/router';

const delay = 500;

// 'entering' lasts a single tick, so the browser would never paint it and the incoming slide
// would animate from where it was parked while hidden (the exit side). Reading layout here
// commits the 'entering' start point, so 'entered' animates from it.
const forceReflow = (node: HTMLElement) => node?.offsetHeight;

export default function SliderItem({ active, direction, serverTime, ...item }: SliderItemProps) {
  const images = {
    desktop: item.image_path,
    mobile: item.mobile_image_path,
  };
  return (
    <Transition in={active} timeout={{ enter: 0, exit: delay }} onEntering={forceReflow}>
      {(state) => (
        // Inactive slides stay mounted (for smooth transitions) but are hidden from
        // AT and removed from the tab order via aria-hidden + inert. The markup (incl.
        // <img> and <a href>) stays in the DOM, so it remains crawlable for SEO.
        <Container
          style={{ ...getTransitionStyles(state, direction) }}
          aria-hidden={!active}
          {...(!active ? ({ inert: '' } as any) : {})}
        >
          <Images images={images} priority={active} />
          <GradientX />
          {/* <GradientY />*/}
          <SlideBody serverTime={serverTime} {...item} />
        </Container>
      )}
    </Transition>
  );
}

// -------------- //
// SUB-COMPONENTS //
// -------------- //

const SliderMainInfo = ({ logo, title, text, schedule }) => (
  <><Logo url={logo} mb={{ xs: 1, md: 3 }} />
    <SlideTitle data={title} schedule={schedule} mb={1} />
    <SlideText data={text} mb={{ xs: 2, md: 3, lg: 5 }} />
  </>
);

const SlideBody = ({ logo_image_path: logo, title, text, schedule_text: schedule,
  navigation_configuration: config, serverTime, slug, short_id, ...rest }: WidgetItemSlider & Pick<SliderItemProps, 'viewer'>) => {
  const href = getNavigationUrl(config, slug ? slug : title, short_id) || '';
  return <SlideInfo px={globalPadding}>
    {href && href === '#' ? <SliderMainInfo logo={logo} title={title} text={text} schedule={schedule} />
      : <Link href={href}>
        <SliderMainInfo logo={logo} title={title} text={text} schedule={schedule} />
      </Link>
    }
    <SlideActions title={title} schedule_text={schedule} serverTime={serverTime} navigation_configuration={config} href={href} short_id={short_id} {...rest} />
  </SlideInfo>;
};

const Logo = ({ url, ...rest }: { url?: string } & SpacingProps) => {
  return url ? (
    <LogoImage {...rest}>
      <img src={`${url}?w=300`} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'bottom right' }} />
    </LogoImage>
  ) : null;
};

const SlideTitle = ({ data, schedule, ...rest }: { data?: string; schedule?: string } & SpacingProps) => (
  <Flex direction={{ xs: 'column-reverse', sm: 'row' }} align={{ xs: 'flex-start', sm: 'center' }} {...rest}>
    <Text variant="h2" weight="bold">
      {data}
    </Text>
    {schedule && <Schedule text={schedule} />}
  </Flex>
);

const Schedule = ({ text }: { text: string }) => (
  <Tag my={1} mr={{ sm: 1 }} color="info">
    {text}
  </Tag>
);

const SlideText = ({ data, ...rest }: { data?: string } & SpacingProps) => (
  <Text weight="demibold" {...rest} style={{ opacity: 0.9 }}>
    {data}
  </Text>
);

/** The content a slide's play button opens. */
export const getSlideContentId = ({ short_id, navigation_configuration }: Pick<WidgetItemSlider, 'short_id' | 'navigation_configuration'>) => (
  short_id || navigation_configuration?.content_id || ''
);

const SlideActions = ({
  href,
  short_id,
  navigation_button: btn,
  navigation_configuration: config,
  show_play_button: playBtn,
  count_down: countdown,
  serverTime,
  flag,
  title,
  schedule_text,
  conditional_flag,
  viewer,
  carrier_video_content_button_text: carrierBtnText,
}: SlideActionsProps) => {
  const id = getSlideContentId({ short_id, navigation_configuration: config });
  const router = useRouter();
  const { authorized } = useRenderedUser();

  // Read on the first render (the server's time is the page's), so the server's HTML already shows the
  // countdown and leaves out the play button, rather than both changing after hydration.
  const [timer, setTimer] = useState(() => getTimerDuration(countdown, serverTime));

  const { free, isCinema } = inspectFlags([conditional_flag, flag].join(', '));
  // Like the content page, a carrier-specific content the viewer can watch is labelled with its carrier's
  // text, read from the viewer's own flags as `hasAccess` is. Without that text it keeps its usual label.
  const hasSpecificCarrie = !!carrierBtnText && inspectFlags(viewer?.flags ?? '').hasSpecificCarrie;
  const [handleWatch, { action }] = usePlayerService({ id, free, isCinema, hasAccess: viewer?.hasAccess, hasSpecificCarrie });
  // A short opens on its own page, which locks the episodes the viewer can't watch itself.
  const isShort = config?.navigation_type === 'content_short';
  // A cinema-online content is sold by ticket, and its button follows the viewer's ticket like the
  // cinema-online page's: `not_active` (none bought) offers to buy one, `pending` (bought, showtime
  // not begun) tells when it starts, and `active` plays it straight away.
  const ticket = viewer?.ticket;
  // A guest's slide has its ticket but not the `content_has_ticket` flag (a guest's flags are all `none`),
  // so its ticket alone tells it's sold by ticket, as on the cinema-online page.
  const soldByTicket = !authorized || inspectFlags(viewer?.flags ?? '').hasTicket;
  const canBuyTicket = !isShort && soldByTicket && ticket?.status === 'not_active';
  const hasPendingTicket = !isShort && ticket?.status === 'pending';
  const hasActiveTicket = !isShort && ticket?.status === 'active';
  // Like the cinema-online page, a guest logs in first and then lands on the ticket page.
  const ticketUrl = authorized ? `/tickets/${id}` : `/login?redirect=/tickets/${id}`;
  const handlePlay = id && playBtn ? () => {
    if (isShort) router.push(href);
    else if (hasActiveTicket) router.push(getPlayerUrl({ id }));
    else handleWatch('p');
  } : undefined;
  // Like the content page's play button: a viewer who must log in or subscribe, or who watches it
  // free or through their carrier, is told so; one who can just play it (or isn't known yet) gets the slide's own title.
  const playLabel = !isShort && !hasActiveTicket && action && action !== 'watchAccess'
    ? __t(...getPlayLabel(action, undefined, undefined, carrierBtnText)) : undefined;
  // A viewer who can play it sees the play icon, as on the content page.
  const showIcon = !playLabel || action === 'carrierWatch';
  const linkDescriptor = (title || schedule_text)?.replace(/\s*\|\s*/g, '، ');

  const renderPlayButton = () => {
    if (canBuyTicket) {
      return (
        <Button as={Link} href={ticketUrl} variant="primaryB" ml={2}>
          {__t('cinema-online.purchase')}
        </Button>
      );
    }
    if (hasPendingTicket) {
      return (
        <Button variant="primaryB" ml={2} style={{ cursor: 'default' }}>
          {ticket?.activation_date_time_text
            ? __t('content.watch.start.cinema', { customText: ticket.activation_date_time_text })
            : __t('cinema-online.start_time')}
        </Button>
      );
    }
    return (
      <Button onClick={handlePlay} variant="primaryA" iconStart={showIcon} ml={2}>
        {showIcon && (
          <Icon cursor="pointer">
            <IconPlay />
          </Icon>
        )}
        {playLabel || btn?.title || __t('content.watch')}
      </Button>
    );
  };
  return (
    <Flex direction={{ xs: 'column', md: 'row' }} align={{ xs: 'flex-start', md: 'normal' }}>
      {!!timer && (
        <Box ml={2} mb={{ xs: 2, md: 0 }}>
          <CountdownTimer value={timer} onFinishTimer={() => setTimer(undefined)} />
        </Box>
      )}
      <Flex>
        {/* As on the content page, there's nothing to play (or buy) until the countdown ends. The API also
            sends `show_play_button: false` until then, so a slide gets its button on the next load. */}
        {handlePlay && !timer && renderPlayButton()}
        {!href || href === '#' ? null : (
          <Button
            as={Link}
            href={href}
            aria-label={
              linkDescriptor
                ? `${__t('common.more_info')} ${__t('common.about')} ${linkDescriptor}`
                : __t('common.more_info')
            }
            variant="secondary"
            iconStart
          >
            <Icon cursor="pointer">
              <IconInfo />
            </Icon>
            {__t('common.more_info')}
            {linkDescriptor && (
              <VisuallyHidden>
                {` ${__t('common.about')} ${linkDescriptor}`}
              </VisuallyHidden>
            )}
          </Button>
        )}
      </Flex>
    </Flex>
  );
};

// ------ //
// STYLES //
// ------ //

export const itemWrapperOffsetBottom = {
  xs: '-2.4rem',
  sm: '20%',
  md: '31%',
  lg: '36%',
};

// Both slides move toward `direction`: the outgoing one leaves to that side and the
// incoming one comes in from the opposite side.
const getTransitionStyles = (state: string, direction: 'right' | 'left') => {
  const shift = direction === 'left' ? -5 : 5;
  const common = {
    opacity: 1,
    transition: `transform ${delay}ms ease-in-out, opacity ${delay}ms ease-in-out`,
  };

  switch (state) {
    case 'entering':
      // Start point, placed without a transition (see `forceReflow`).
      return {
        ...common,
        zIndex: 2,
        opacity: 0,
        transform: `translateX(${-shift}%)`,
        transition: 'none',
      };
    case 'entered':
      return {
        ...common,
        zIndex: 2,
        opacity: 1,
        transform: 'translateX(0)',
      };
    case 'exiting':
    case 'exited':
      return {
        ...common,
        zIndex: 0,
        opacity: 0,
        transform: `translateX(${shift}%)`,
      };
    default:
      return common;
  }
};

const Container = styled.div(({ theme }) => ({
  position: 'absolute',
  top: 0,
  right: 0,
  left: 0,
  bottom: 0,
  width: '100%',
  height: '100%',
  background: 'no-repeat center url(/static/images/placeholder/slider-xs.svg)',
  backgroundSize: 'cover',
  [theme.breakpoints.up('sm')]: {
    backgroundImage: 'url(/static/images/placeholder/slider.svg)',
  },
}));

// `priority` is true only for the active slide: it loads eagerly with `fetchpriority="high"`
// as the LCP image. Inactive slides are `lazy` + "low" so the preload scanner skips them;
// they still load right after layout since they sit inside the viewport.
const Images = ({ images, priority = false }: { images: SliderItemImages; priority?: boolean }) => (
  <picture>
    {desktopSources.map(({ minWidth, width }) => (
      <source key={minWidth} media={`(min-width: ${minWidth}px)`} srcSet={`${images.desktop}?w=${width}`} />
    ))}
    <SliderImg
      src={getFallbackSrc(images)}
      alt=""
      loading={priority ? 'eager' : 'lazy'}
      {...({ fetchpriority: priority ? 'high' : 'low' } as any)}
    />
  </picture>
);

// Ordered widest first, matching how <picture> picks the first matching <source>.
const desktopSources = [
  { minWidth: 1280, width: 1920 },
  { minWidth: 960, width: 1125 },
  { minWidth: 600, width: 960 },
  { minWidth: 480, width: 660 },
];

const getFallbackSrc = (images: SliderItemImages) => `${images.mobile || images.desktop}?w=480`;

// Preload media ranges must not overlap, otherwise a browser downloads more than one size.
const belowWidth = (px: number) => `(max-width: ${px - 0.02}px)`;

export const SliderImagePreload = ({ images }: { images: SliderItemImages }) => (
  <Head>
    {desktopSources.map(({ minWidth, width }, i) => {
      const upper = desktopSources[i - 1]?.minWidth;
      const media = upper ? `(min-width: ${minWidth}px) and ${belowWidth(upper)}` : `(min-width: ${minWidth}px)`;
      return (
        <link
          key={`slider-preload-${minWidth}`}
          rel="preload"
          as="image"
          href={`${images.desktop}?w=${width}`}
          media={media}
          {...({ fetchpriority: 'high' } as any)}
        />
      );
    })}
    <link
      key="slider-preload-fallback"
      rel="preload"
      as="image"
      href={getFallbackSrc(images)}
      media={belowWidth(desktopSources[desktopSources.length - 1].minWidth)}
      {...({ fetchpriority: 'high' } as any)}
    />
  </Head>
);

// @emotion/is-prop-valid 1.1 doesn't know `fetchpriority` and would strip it from the <img>.
const SliderImg = styled('img', {
  shouldForwardProp: (prop) => prop !== 'theme' && prop !== 'as',
})(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
}));

const GradientX = styled.div(({ theme }) => ({
  zIndex: 0,
  position: 'absolute',
  right: 0,
  left: 0,
  bottom: -1,
  height: '36%',
  background: `linear-gradient(to top, ${theme.palette.background[mainBg][0]}, rgba(0,0,0,0))`,
}));

// const GradientY = styled.div(({ theme }) => ({
//   [theme.breakpoints.up('sm')]: {
//     zIndex: 0,
//     position: 'absolute',
//     right: 0,
//     top: 0,
//     height: '100%',
//     width: '50%',
//     background: `linear-gradient(to left, ${theme.palette.background[mainBg][0]}, rgba(0,0,0,0))`,
//   },
// }));

const SlideInfo = styled(Box)(({ theme }) => ({
  position: 'absolute',
  zIndex: 1,
  ...theme.mixins.create(itemWrapperOffsetBottom, (bottom) => ({ bottom })),
}));

const logoSizes = {
  xs: 90,
  md: 120,
};

const LogoImage = styled(Box)(({ theme }) => ({
  ...theme.mixins.create(logoSizes, (height) => ({
    height: `${height / 16}rem`,
    width: `${(height / 16) * 2}rem`,
  })),
}));

// ----- //
// TYPES //
// ----- //

interface SliderItemProps extends WidgetItemSlider {
  active: boolean;
  direction: 'right' | 'left';
  serverTime?: undefined | string
  /** The viewer's own conditional props for the slide's content; undefined while unknown. */
  viewer?: ViewerContentProps
}

export interface SliderItemImages {
  desktop: string;
  mobile?: string;
}

type SlideActionsProps = Pick<
  WidgetItemSlider,
  'navigation_button' | 'navigation_configuration' | 'show_play_button' | 'count_down' | 'title' |'schedule_text' | 'slug' | 'short_id' | 'carrier_video_content_button_text' | 'conditional_flag' | 'flag'
> & Pick<SliderItemProps, 'viewer'> & {
  serverTime?: string | undefined,
  href: string
};
